import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Crosshair, ArrowLeft } from "lucide-react";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, signup, user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { if (user) nav("/app"); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        await signup(name, email, password);
        toast.success("Welcome to FinLens.");
      } else {
        await login(email, password);
        toast.success("Logged in.");
      }
      nav("/app");
    } catch (err) {
      toast.error(errorMessage(err, "Failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] grid lg:grid-cols-2">
      <div className="hidden lg:block relative border-r-2 border-strong bg-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/33435450/pexels-photo-33435450.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }} />
        <div className="relative h-full flex flex-col p-12">
          <Link to="/" className="flex items-center gap-2 mb-auto" data-testid="auth-back">
            <Crosshair className="w-5 h-5" /> <span className="font-display font-black text-xl">FINLENS</span>
          </Link>
          <div className="max-w-md">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">/ Forensic Intelligence</div>
            <h2 className="font-display font-black text-5xl tracking-tighter leading-[0.9] mb-6">
              The numbers tell one story.<br />
              <span className="text-[#FF2A00]">The footnotes tell another.</span>
            </h2>
            <p className="text-sm text-zinc-300 leading-relaxed">
              Every annual report hides red flags. Auditor remarks. Related-party deals.
              Aggressive accounting. We surface them in 90 seconds.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md bg-white border-2 border-strong p-8 sm:p-10 shadow-brutal">
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-black mb-8" data-testid="auth-back-home">
            <ArrowLeft className="w-3 h-3" /> Back
          </Link>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">/ {mode === "signup" ? "Create Account" : "Sign In"}</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mb-8">
            {mode === "signup" ? "Open the lens." : "Welcome back."}
          </h1>

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Name</label>
                <input
                  data-testid="auth-name-input"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border-2 border-strong p-3 mt-1 focus:border-[#002FA7] outline-none bg-white"
                  placeholder="Jane Analyst"
                />
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email</label>
              <input
                data-testid="auth-email-input"
                required type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border-2 border-strong p-3 mt-1 focus:border-[#002FA7] outline-none"
                placeholder="you@firm.com"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Password</label>
              <input
                data-testid="auth-password-input"
                required type="password" minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border-2 border-strong p-3 mt-1 focus:border-[#002FA7] outline-none"
                placeholder="Min 8 characters"
              />
            </div>
            <button
              data-testid="auth-submit"
              disabled={busy}
              className="w-full bg-black text-white py-3.5 text-sm uppercase tracking-[0.2em] hover:bg-[#002FA7] transition-colors disabled:opacity-50"
            >
              {busy ? "Working…" : (mode === "signup" ? "Create Account" : "Sign In")}
            </button>
          </form>

          <button
            data-testid="auth-toggle-mode"
            onClick={() => setMode(mode === "signup" ? "login" : "signup")}
            className="mt-6 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-black"
          >
            {mode === "signup" ? "Already have an account? → Sign in" : "New here? → Create account"}
          </button>
        </div>
      </div>
    </div>
  );
}
